export interface FlatStepConfig {
  id: string;
  title: string;
  path: string;
  group: string;
  groupTitle: string;
  /** If true, navigating away from this step will replace the history entry instead of pushing */
  skipHistory?: boolean;
}

export const FLAT_STEP_CONFIGS: FlatStepConfig[] = [
  {
    id: 'overview',
    title: 'Overview',
    path: 'overview',
    group: 'overview',
    groupTitle: 'Overview',
  },
  {
    id: 'assets',
    title: 'Select Assets',
    path: 'assets',
    group: 'assets',
    groupTitle: 'Select Assets',
  },
  {
    id: 'assessment-intro',
    title: 'Introduction',
    path: 'assessment-intro',
    group: 'assessment',
    groupTitle: 'Coverage Assessment',
  },
  {
    id: 'assessment-accounts',
    title: 'Account Details',
    path: 'assessment-accounts',
    group: 'assessment',
    groupTitle: 'Coverage Assessment',
  },
  {
    id: 'assessment-projection',
    title: 'Projection',
    path: 'assessment-projection',
    group: 'assessment',
    groupTitle: 'Coverage Assessment',
  },
  {
    id: 'preferences-death-benefits',
    title: 'Death Benefits',
    path: 'preferences-death-benefits',
    group: 'preferences',
    groupTitle: 'Policy Preferences',
  },
  {
    id: 'preferences-payment',
    title: 'Payment Preference',
    path: 'preferences-payment',
    group: 'preferences',
    groupTitle: 'Policy Preferences',
  },
  {
    id: 'preferences-benefit-structure',
    title: 'Benefit Structure',
    path: 'preferences-benefit-structure',
    group: 'preferences',
    groupTitle: 'Policy Preferences',
  },
  {
    id: 'options',
    title: 'Policy Types',
    path: 'options',
    group: 'preferences',
    groupTitle: 'Policy Preferences',
  },
  {
    id: 'preferences-premium',
    title: 'Premium Budget',
    path: 'preferences-premium',
    group: 'preferences',
    groupTitle: 'Policy Preferences',
  },
  {
    id: 'quotes-loading',
    title: 'Finding Policies',
    path: 'quotes-loading',
    group: 'preferences',
    groupTitle: 'Policy Preferences',
    skipHistory: true,
  },
  {
    id: 'quotes-selection',
    title: 'Policy Options',
    path: 'quotes-selection',
    group: 'quotes',
    groupTitle: 'Policy Options',
  },
  {
    id: 'quotes-funding',
    title: 'Policy Funding',
    path: 'quotes-funding',
    group: 'quotes',
    groupTitle: 'Policy Options',
  },
  {
    id: 'plan-comparison',
    title: 'Plan Comparison',
    path: 'plan-comparison',
    group: 'plan',
    groupTitle: 'Plan',
  },
];

export const STEP_GROUPS = [...new Set(FLAT_STEP_CONFIGS.map(s => s.group))];

export const PREMIUM_BUDGET_STEP_ID = 'preferences-premium';
export const PAYMENT_PREFERENCE_STEP_ID = 'preferences-payment';
export const OPTIONS_STEP_ID = 'options';

/** Preference questions in wizard order (premium omitted when feature flag is off). */
export const PREFERENCE_WIZARD_QUESTION_STEP_IDS: readonly string[] = [
  'preferences-death-benefits',
  PAYMENT_PREFERENCE_STEP_ID,
  'preferences-benefit-structure',
  PREMIUM_BUDGET_STEP_ID,
];

export function getActivePreferenceQuestionStepIds(configs: FlatStepConfig[]): string[] {
  return PREFERENCE_WIZARD_QUESTION_STEP_IDS.filter(id => configs.some(c => c.id === id));
}

interface GetFlatStepConfigsOptions {
  premiumBudgetEnabled: boolean;
  isD2C?: boolean;
}

export function getFlatStepConfigs(options: GetFlatStepConfigsOptions): FlatStepConfig[] {
  const { premiumBudgetEnabled, isD2C } = options;

  const stepsToExclude = new Set<string>();

  if (!premiumBudgetEnabled) {
    stepsToExclude.add(PREMIUM_BUDGET_STEP_ID);
  }

  // D2C consumers browse payment options via tabs on the quotes step instead
  // and see all policy types by default (no category selection needed)
  if (isD2C) {
    stepsToExclude.add(PAYMENT_PREFERENCE_STEP_ID);
    stepsToExclude.add(OPTIONS_STEP_ID);
  }

  if (stepsToExclude.size === 0) {
    return FLAT_STEP_CONFIGS;
  }

  return FLAT_STEP_CONFIGS.filter(s => !stepsToExclude.has(s.id));
}

export function getCompletionStepIds(configs: FlatStepConfig[]): GuidedPlanningStep[] {
  return configs
    .filter(s => s.id !== 'quotes-loading')
    .map(s => s.id as GuidedPlanningStep);
}

export function getStepIndex(stepId: string, configs: FlatStepConfig[]): number {
  const index = configs.findIndex(s => s.id === stepId);
  if (index === -1) {
    throw new Error(`Step not found: ${stepId}`);
  }
  return index;
}

export function getStepId(stepIndex: number, configs: FlatStepConfig[]): GuidedPlanningStep {
  const stepConfig = configs[stepIndex];
  if (!stepConfig) {
    throw new Error(`Invalid step index: ${stepIndex}`);
  }
  return stepConfig.id as GuidedPlanningStep;
}

export const ASSET_TYPES = [
  { id: 'roth-ira', label: 'Roth IRA' },
  { id: 'roth-401k', label: 'Roth 401(k)' },
  { id: 'traditional-ira', label: 'Traditional IRA' },
  { id: 'traditional-401k', label: 'Traditional 401(k)' },
  { id: 'hsa', label: 'HSA Account' },
  { id: 'brokerage', label: 'Brokerage Account' },
  { id: 'home-equity', label: 'Home Equity' },
  { id: 'retirement-income', label: 'Retirement Income' },
  { id: 'cryptocurrency', label: 'Cryptocurrency' },
  { id: 'existing-ltc-policy', label: 'Existing LTC Coverage' },
] as const;

export type AssetTypeId = (typeof ASSET_TYPES)[number]['id'];

/**
 * Maps guided planning asset IDs (kebab-case) to the Redux/backend accountType format (camelCase).
 */
export const ASSET_TYPE_TO_ACCOUNT_TYPE: Record<AssetTypeId, string> = {
  'roth-ira': 'rothIra',
  'roth-401k': 'roth401k',
  'traditional-ira': 'traditionalIra',
  'traditional-401k': 'traditional401k',
  'hsa': 'hsa',
  'brokerage': 'brokerage',
  'home-equity': 'homeEquity',
  'retirement-income': 'retirementIncome',
  'cryptocurrency': 'cryptocurrency',
  'existing-ltc-policy': 'existingLtcPolicy',
};
